import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { CredorTipo } from "@atlas/sdk";
import { CredorTipoPill } from "../../lib/credor-tipo";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, DataTable, Pagination, Pill, SelectField, type Column , fmtBRL} from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { useDebouncedValue } from "../../lib/use-debounce";
import { produtoLabelDeContrato } from "@atlas/types";
import { TotalEmFolha, ValorSolicitado } from "../../components/ValorContrato";

// ADF pela averbadora — a averbadora aplica/reporta falha em folha; prefeitura
// so recebe/consulta. Cliente disse: "a averbadora que faz a adf, a prefeitura
// so recebe". Substitui o fluxo antigo (que era de gerar PDFs individuais).


type AdfRow = {
  id: string; adf: string; idUnico: string; cpfMasked: string; matricula: string; nome: string;
  bancoNome: string; unidadeId: number; prefeituraNome: string; competencia: string;
  /** Parceiro, consignatária ou credor a identificar (ADR-0010). */
  credorTipo?: CredorTipo;
  valorParcela: number; totalParcelas: number;
  valorFinanciado?: number;
  tipoContrato?: string;
  /** Bucket de margem — distingue Cartao Consignado de Cartao Beneficio quando
   *  tipoContrato=ECONSIGNADO (mesmos os dois; so tipoMargem separa). */
  tipoMargem?: string;
  /** `atlas` | `legado`. Legado migrado nasce como ADF, mas marcado. */
  origem?: "atlas" | "legado";
  status: "recebida" | "aplicada" | "falha"; motivo?: string;
  /** ISO timestamp em que o ADF entrou/mudou de status na averbadora — usado para ordenar recentes no topo. */
  atualizadoEm?: string;
};

// Rotulo do produto via helper unificado (lib/produto-label). Antes:
// versao local com fallback "Cartao" pra ECONSIGNADO sem tipoMargem —
// nao cobria observacoes com "telemedicina"/"portabilid" nem bancoOrigem.

// A regra de qual numero vale vive em @atlas/types (`valorSolicitado` /
// `totalEmFolha`, feature 012) e os dois componentes de `ValorContrato` a
// desenham nas quatro telas. O zero do tombamento nao pode voltar a virar
// "R$ 0,00" na tela — nem a soma das parcelas voltar a se chamar "Valor".


type StatusFiltro = "" | "recebida" | "aplicada" | "falha";

const CABECALHO_STATUS: Record<StatusFiltro, { label: string; cor: string; hint: string }> = {
  "": { label: "Todas as ADFs", cor: "var(--text)", hint: "recebidas, aplicadas e falhas na mesma lista" },
  recebida: { label: "Recebidas", cor: "var(--gold-500)", hint: "aguardando aplicação — novas ADFs entram no topo" },
  aplicada: { label: "Aplicadas", cor: "var(--emerald-500)", hint: "já em folha" },
  falha: { label: "Falhas", cor: "var(--danger-500)", hint: "precisam ser reprocessadas ou corrigidas" },
};

/** Chip de filtro rapido — um clique liga/desliga o corte. */
function Chip({ ativo, onClick, cor, children }: { ativo: boolean; onClick: () => void; cor?: string; children: ReactNode }) {
  const c = cor ?? "var(--accent)";
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "5px 11px", borderRadius: 999, cursor: "pointer", fontSize: 12.5,
        border: `1px solid ${ativo ? c : "var(--border)"}`,
        background: ativo ? `color-mix(in srgb, ${c} 16%, transparent)` : "var(--surface)",
        color: ativo ? "var(--text)" : "var(--text-muted)",
        fontWeight: ativo ? 700 : 500,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

/** Checkbox de cabecalho com estado indeterminado (parte da pagina marcada). */
function CheckboxTodos({ marcado, parcial, onChange, title }: {
  marcado: boolean; parcial: boolean; onChange: (v: boolean) => void; title: string;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  // `indeterminate` nao existe como atributo — so' via DOM.
  useEffect(() => { if (ref.current) ref.current.indeterminate = parcial; }, [parcial]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={marcado}
      onChange={(e) => onChange(e.target.checked)}
      title={title}
      aria-label={title}
      style={{ cursor: "pointer" }}
    />
  );
}

export function AdminAdf() {
  const qc = useQueryClient();
  const comps = useQuery({ queryKey: ["admin", "adf-comps"], queryFn: () => atlas.admin.adfCompetencias() });
  const [competencia, setCompetencia] = useState<string>("");
  const [prefeituraFiltro, setPrefeituraFiltro] = useState<string>("");
  // Filtro livre por matricula OU CPF. Comparacao por SUBSTRING dos digitos —
  // funciona pra CPF mascarado (***.***.***-33 casa com "33"), matricula
  // parcial e nome (o `busca` como digitado, sem mascarar, tenta match no nome).
  const [busca, setBusca] = useState<string>("");
  // Sem debounce cada tecla e uma queryKey nova, ou seja um request.
  const buscaDebounced = useDebouncedValue(busca);
  // Filtros rapidos (pedido do cliente 29/08/2026). Status vira FILTRO no lugar
  // das tres secoes separadas que a tela tinha — o default `recebida` preserva
  // a intencao original (16/07/2026) de a ADF nova ter um lugar previsivel pra
  // chegar, agora sem obrigar a rolar por cima das aplicadas.
  const [statusFiltro, setStatusFiltro] = useState<StatusFiltro>("recebida");
  const [origemFiltro, setOrigemFiltro] = useState<"" | "atlas" | "legado">("");
  const [produtoFiltro, setProdutoFiltro] = useState<string>("");
  const [bancoFiltro, setBancoFiltro] = useState<string>("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(25);
  // Trocar filtro com a posicao antiga cairia num offset que ja nao existe.
  const filtrar = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPagina(1); };

  useEffect(() => {
    if (!competencia && comps.data?.competenciaAtual) setCompetencia(comps.data.competenciaAtual);
  }, [comps.data, competencia]);

  const adfsQ = useQuery({
    queryKey: ["admin", "adf", competencia, prefeituraFiltro, bancoFiltro, produtoFiltro,
               buscaDebounced, statusFiltro, origemFiltro, pagina, porPagina],
    queryFn: () => atlas.admin.adfList({
      competencia,
      prefeitura: prefeituraFiltro || undefined,
      banco: bancoFiltro || undefined,
      produto: produtoFiltro || undefined,
      q: buscaDebounced || undefined,
      status: statusFiltro || undefined,
      origem: origemFiltro || undefined,
      limit: porPagina,
      offset: (pagina - 1) * porPagina,
    }),
    enabled: !!competencia,
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
    // Sem isto a tabela pisca vazia a cada virada de pagina.
    placeholderData: (anterior) => anterior,
  });

  // FACETAS, CONTAGENS E IDS SELECIONAVEIS VEM DA API desde a 007. Os tres
  // falam da competencia ou do RECORTE, e a tela so tem a pagina — calcula-los
  // aqui daria chips contando 25 linhas e um "marcar tudo" que marca so a
  // pagina, os dois plausiveis e errados (FR-014).
  const facetas = adfsQ.data?.facetas ?? { prefeituras: [], bancos: [], produtos: [] };
  const prefsUnicas = facetas.prefeituras;
  const bancosUnicos = facetas.bancos;
  const produtosUnicos = facetas.produtos;
  const contagemStatus = adfsQ.data?.contagemStatus ?? { todas: 0, recebida: 0, aplicada: 0, falha: 0 };
  const contagemOrigem = adfsQ.data?.contagemOrigem ?? { todas: 0, atlas: 0, legado: 0 };

  // A pagina JA vem filtrada e ordenada (mais recente no topo).
  const daPagina = adfsQ.data?.adfs ?? [];
  const totalDoRecorte = adfsQ.data?.total ?? 0;

  const [sel, setSel] = useState<Set<string>>(new Set());
  // Trocar de FILTRO zera a selecao (o que estava marcado saiu de vista).
  // Trocar de PAGINA nao — e' justamente o que faz "selecionar todas do filtro"
  // valer a pena.
  //
  // `porPagina` ENTROU aqui, e nao e' o mesmo caso de `pagina`: mudar o tamanho
  // da pagina muda o RECORTE, e com ele o `idsSelecionaveis` que a API devolve.
  // Sem isto uma selecao feita antes da troca sobrevivia apontando para ids que
  // sairam do recorte — e a acao em lote seguiria com eles. `pagina` navega
  // dentro do mesmo recorte; `porPagina` troca o recorte.
  useEffect(() => {
    setSel(new Set());
  }, [competencia, prefeituraFiltro, busca, statusFiltro, origemFiltro, produtoFiltro, bancoFiltro, porPagina]);

  // So' ADF `recebida` pode ser aplicada/reprovada — e' o unico status selecionavel.
  const selecionaveisDaPagina = useMemo(() => daPagina.filter((a) => a.status === "recebida"), [daPagina]);
  // OS IDS DO FILTRO INTEIRO VEM DA API: e o que faz "marcar o filtro todo"
  // sobreviver a paginacao. Sem isso a tela so conseguiria marcar a pagina, e
  // fechar uma competencia viraria virar pagina marcando 25 por vez.
  const idsDoFiltro = adfsQ.data?.idsSelecionaveis ?? [];
  const paginaToda = selecionaveisDaPagina.length > 0 && selecionaveisDaPagina.every((a) => sel.has(a.id));
  const paginaParcial = !paginaToda && selecionaveisDaPagina.some((a) => sel.has(a.id));
  const filtroTodo = idsDoFiltro.length > 0 && idsDoFiltro.every((id) => sel.has(id));

  const marcarPagina = (v: boolean) => {
    const n = new Set(sel);
    for (const a of selecionaveisDaPagina) (v ? n.add(a.id) : n.delete(a.id));
    setSel(n);
  };
  // Nao substitui a selecao por um conjunto VAZIO. `idsDoFiltro` e'
  // `idsSelecionaveis` da API (`?? []`), entao ele chega vazio tambem quando a
  // resposta nao trouxe o campo — erro, versao antiga da rota, ou
  // `placeholderData` de um recorte anterior. Nesses casos o `new Set([])`
  // ESVAZIAVA a selecao em silencio: o operador clica "marcar o filtro todo" e
  // perde o que ja tinha marcado, sem aviso e sem erro. Clique sem ids e' um
  // no-op, nao um "desmarcar tudo".
  const marcarFiltroInteiro = () => {
    if (idsDoFiltro.length === 0) return;
    setSel(new Set(idsDoFiltro));
  };

  const confirmar = useMutation({
    mutationFn: (ids: string[]) => atlas.admin.confirmarAdfAdmin(ids),
    onSuccess: (r, ids) => {
      // Cliente 31/07/2026: se backend responde aplicadas < selecionadas, mostra
      // aviso. Antes o silencio induzia o operador a achar que aplicou tudo.
      const aplicadas = (r as { aplicadas?: number } | undefined)?.aplicadas ?? 0;
      if (aplicadas < ids.length) {
        alert(`Apenas ${aplicadas} de ${ids.length} ADFs foram aplicadas. As restantes podem estar em competencia diferente da corrente ou ja processadas — recarregue a lista e tente novamente.`);
      }
      setSel(new Set());
      qc.invalidateQueries({ queryKey: ["admin", "adf"] });
      qc.invalidateQueries({ queryKey: ["admin", "adf-comps"] });
    },
    onError: (err) => {
      alert(`Falha ao aplicar ADFs: ${(err as Error).message}`);
    },
  });
  const falha = useMutation({
    mutationFn: ({ ids, motivo }: { ids: string[]; motivo: string }) => atlas.admin.reportarFalhaAdfAdmin(ids, motivo),
    onSuccess: () => {
      setSel(new Set());
      qc.invalidateQueries({ queryKey: ["admin", "adf"] });
      qc.invalidateQueries({ queryKey: ["admin", "adf-comps"] });
    },
    onError: (err) => {
      alert(`Falha ao reportar falha: ${(err as Error).message}`);
    },
  });

  // OS TOTAIS VEM DA API: falam do RECORTE inteiro, e a tela so tem a pagina.
  // Soma-los aqui daria KPI de 25 linhas — menor, plausivel e errado (FR-014).
  const totalParcelas = adfsQ.data?.totais?.parcelas ?? 0;
  const totalFinanciado = adfsQ.data?.totais?.financiado ?? 0;
  const resumo = useMemo(() => {
    const s = adfsQ.data?.resumo;
    return { r: s?.recebida ?? 0, a: s?.aplicada ?? 0, f: s?.falha ?? 0 };
  }, [adfsQ.data]);

  const algumFiltroLigado = !!(prefeituraFiltro || busca || origemFiltro || produtoFiltro || bancoFiltro) || statusFiltro !== "recebida";
  const limparFiltros = () => {
    setPrefeituraFiltro(""); setBusca(""); setStatusFiltro("recebida");
    setOrigemFiltro(""); setProdutoFiltro(""); setBancoFiltro(""); setPagina(1);
  };

  const columns: Column<AdfRow>[] = [
    {
      key: "sel",
      header: (
        <CheckboxTodos
          marcado={paginaToda}
          parcial={paginaParcial}
          onChange={marcarPagina}
          title={
            selecionaveisDaPagina.length === 0
              ? "Nenhuma ADF selecionável nesta página"
              : paginaToda
                ? "Desmarcar as desta página"
                : `Selecionar as ${selecionaveisDaPagina.length} recebidas desta página`
          }
        />
      ),
      render: (a) => (
        a.status === "recebida" ? (
          <input
            type="checkbox"
            checked={sel.has(a.id)}
            onChange={(e) => {
              const n = new Set(sel);
              e.target.checked ? n.add(a.id) : n.delete(a.id);
              setSel(n);
            }}
          />
        ) : null
      ),
    },
    { key: "adf", header: "ADF", mono: true },
    { key: "idUnico", header: "ID único", mono: true },
    { key: "prefeitura", header: "Órgão/entidade", render: (a) => a.prefeituraNome },
    { key: "banco", header: "Credor", render: (a) => (
      <>
        <div>{a.bancoNome}</div>
        <CredorTipoPill tipo={a.credorTipo} />
      </>
    ) },
    { key: "cpfMasked", header: "CPF", mono: true },
    { key: "nome", header: "Servidor" },
    {
      key: "categoria",
      header: "Categoria",
      render: (a) => (
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
          color: "var(--accent)", padding: "2px 8px", borderRadius: 999,
          border: "1px solid color-mix(in srgb, var(--accent) 40%, transparent)",
          background: "color-mix(in srgb, var(--accent) 10%, transparent)",
        }}>
          {produtoLabelDeContrato(a)}
        </span>
      ),
    },
    {
      key: "origem",
      header: "Origem",
      // Visível de propósito. Na carga inicial o lote traz a base do município
      // inteira; sem esta coluna parece que centenas de averbações nasceram
      // naquele mês, quando na verdade é dívida que já descontava e passou a
      // ser instruída pela Atlas.
      render: (a) => (a.origem === "legado" ? (
        <Pill variant="pendente">legado</Pill>
      ) : (
        <span style={{ opacity: 0.55, fontSize: 12 }}>Atlas</span>
      )),
    },
    { key: "valorParcela", header: "Parcela", align: "right", render: (a) => fmtBRL(a.valorParcela) },
    // Duas colunas com nome proprio (feature 012): o principal, que no legado
    // sem informe e' "—" com o selo, e o que sai da folha, que e' sempre
    // conhecido. Antes uma coluna "Valor total" misturava os dois.
    {
      key: "valorFinanciado",
      header: "Valor solicitado",
      align: "right",
      render: (a) => <ValorSolicitado contrato={a} formatar={fmtBRL} />,
    },
    {
      key: "totalEmFolha",
      header: "Total em folha",
      align: "right",
      render: (a) => <TotalEmFolha contrato={a} formatar={fmtBRL} />,
    },
    {
      key: "status",
      header: "Status",
      render: (a) => (
        <div>
          <Pill variant={a.status === "aplicada" ? "averbado" : a.status === "falha" ? "expirado" : "pendente"}>
            {a.status}
          </Pill>
          {a.motivo ? <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 2 }}>{a.motivo}</div> : null}
        </div>
      ),
    },
  ];

  const cab = CABECALHO_STATUS[statusFiltro];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Averbadora
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem", letterSpacing: "-0.02em" }}>ADF — Autorização de Desconto em Folha</h1>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 720 }}>
          Visão consolidada de todas as organizações. A averbadora aplica em folha ou reporta falha —
          o órgão só recebe/consulta. A consignatária vê o estado da ADF automaticamente pelo contrato.
        </p>
      </header>

      <Card>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "var(--text-muted)" }}>Competência:</span>
          {(comps.data?.competencias ?? []).map((c) => (
            <button
              key={c.competencia}
              onClick={() => filtrar(setCompetencia)(c.competencia)}
              style={{
                padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontSize: 13,
                border: `1px solid ${competencia === c.competencia ? "var(--accent)" : "var(--border)"}`,
                background: competencia === c.competencia ? "color-mix(in srgb, var(--accent) 14%, transparent)" : "var(--surface)",
                color: "var(--text)",
              }}
            >
              {c.competencia} <span style={{ color: "var(--text-muted)" }}>({c.aplicadas}/{c.total})</span>
            </button>
          ))}
          {(comps.data?.competencias.length ?? 0) === 0 ? (
            <span style={{ fontSize: 13 }}>{comps.data?.competenciaAtual ?? "—"}</span>
          ) : null}
        </div>

        {/* Filtros rapidos — um clique. Status substituiu as tres secoes que a
            tela tinha; Origem separa averbacao nova de tombamento, que e' o
            corte que mais muda a lista depois de uma carga inicial. */}
        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "var(--text-muted)", minWidth: 62 }}>Status:</span>
          <Chip ativo={statusFiltro === "recebida"} cor="var(--gold-500)" onClick={() => filtrar(setStatusFiltro)("recebida")}>
            Recebidas ({contagemStatus.recebida})
          </Chip>
          <Chip ativo={statusFiltro === "aplicada"} cor="var(--emerald-500)" onClick={() => filtrar(setStatusFiltro)("aplicada")}>
            Aplicadas ({contagemStatus.aplicada})
          </Chip>
          <Chip ativo={statusFiltro === "falha"} cor="var(--danger-500)" onClick={() => filtrar(setStatusFiltro)("falha")}>
            Falhas ({contagemStatus.falha})
          </Chip>
          <Chip ativo={statusFiltro === ""} onClick={() => filtrar(setStatusFiltro)("")}>
            Todas ({contagemStatus.todas})
          </Chip>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "var(--text-muted)", minWidth: 62 }}>Origem:</span>
          <Chip ativo={origemFiltro === ""} onClick={() => filtrar(setOrigemFiltro)("")}>
            Todas ({contagemOrigem.todas})
          </Chip>
          <Chip ativo={origemFiltro === "atlas"} onClick={() => filtrar(setOrigemFiltro)("atlas")}>
            Atlas ({contagemOrigem.atlas})
          </Chip>
          <Chip ativo={origemFiltro === "legado"} cor="var(--gold-500)" onClick={() => filtrar(setOrigemFiltro)("legado")}>
            Tombamento ({contagemOrigem.legado})
          </Chip>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ minWidth: 180 }}>
            <SelectField
              label="Prefeitura"
              value={prefeituraFiltro}
              onChange={(e) => filtrar(setPrefeituraFiltro)(e.target.value)}
              options={[{ value: "", label: "— TODAS —" }, ...prefsUnicas.map((p) => ({ value: p, label: p }))]}
            />
          </div>
          <div style={{ minWidth: 170 }}>
            <SelectField
              label="Consignatária"
              value={bancoFiltro}
              onChange={(e) => filtrar(setBancoFiltro)(e.target.value)}
              options={[{ value: "", label: "— TODOS —" }, ...bancosUnicos.map((b) => ({ value: b, label: b }))]}
            />
          </div>
          <div style={{ minWidth: 170 }}>
            <SelectField
              label="Produto"
              value={produtoFiltro}
              onChange={(e) => filtrar(setProdutoFiltro)(e.target.value)}
              options={[{ value: "", label: "— TODOS —" }, ...produtosUnicos.map((p) => ({ value: p, label: p }))]}
            />
          </div>
          <label style={{ fontSize: 13, color: "var(--text-muted)", display: "flex", gap: 6, alignItems: "center", flex: "1 1 240px", minWidth: 200 }}>
            Buscar:
            <div style={{ position: "relative", flex: 1 }}>
              <input
                type="search"
                value={busca}
                onChange={(e) => filtrar(setBusca)(e.target.value)}
                placeholder="Matrícula, CPF ou nome"
                style={{
                  width: "100%", padding: "6px 30px 6px 10px", borderRadius: 8,
                  border: "1px solid var(--border-strong)",
                  background: "var(--bg-elev)", color: "var(--text)", fontSize: 13,
                  boxSizing: "border-box",
                }}
              />
              {busca ? (
                <button
                  type="button"
                  onClick={() => filtrar(setBusca)("")}
                  aria-label="Limpar busca"
                  title="Limpar"
                  style={{
                    position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                    background: "transparent", border: "none", cursor: "pointer",
                    color: "var(--text-dim)", fontSize: 16, lineHeight: 1, padding: "2px 4px",
                  }}
                >
                  ×
                </button>
              ) : null}
            </div>
          </label>
          {algumFiltroLigado ? (
            <Button size="sm" variant="ghost" onClick={limparFiltros}>↻ Limpar filtros</Button>
          ) : null}
        </div>

        <div style={{ marginTop: 12, fontSize: 13, color: "var(--text-muted)" }}>
          {/* "valor do recorte" e' o TOTALIZADOR (`somarValorTotal` na API):
              principal onde a Atlas o conhece, total em folha onde nao. E' um
              agregado, nao uma coluna — e por isso nao se chama "valor
              solicitado" nem "total em folha". */}
          {totalDoRecorte} ADFs · total parcelas <b style={{ color: "var(--text)" }}>{fmtBRL(totalParcelas)}</b> · valor do recorte <b style={{ color: "var(--emerald-500)" }}>{fmtBRL(totalFinanciado)}</b>
          {" · "}
          <span style={{ color: "var(--gold-500)" }}>{resumo.r} recebidas</span>
          {" / "}
          <span style={{ color: "var(--emerald-500)" }}>{resumo.a} aplicadas</span>
          {" / "}
          <span style={{ color: "var(--danger-500)" }}>{resumo.f} falhas</span>
        </div>
      </Card>

      {sel.size > 0 ? (
        <Card style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <b style={{ fontSize: 14 }}>{sel.size} selecionada(s)</b>
          {/* A selecao para na pagina por padrao. Quando a pagina inteira esta
              marcada e ha mais recebidas no filtro, oferece esticar pro filtro
              todo — sem isso "aplicar tudo" viraria virar pagina a pagina. */}
          {paginaToda && !filtroTodo ? (
            <button
              type="button"
              onClick={marcarFiltroInteiro}
              style={{
                background: "transparent", border: "none", cursor: "pointer", padding: 0,
                color: "var(--accent)", fontSize: 13, fontWeight: 600, textDecoration: "underline",
              }}
            >
              Selecionar todas as {idsDoFiltro.length} recebidas do filtro
            </button>
          ) : null}
          {filtroTodo && idsDoFiltro.length > selecionaveisDaPagina.length ? (
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              todas as recebidas do filtro estão selecionadas
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => setSel(new Set())}
            style={{
              background: "transparent", border: "none", cursor: "pointer", padding: 0,
              color: "var(--text-muted)", fontSize: 12.5, textDecoration: "underline",
            }}
          >
            limpar seleção
          </button>
          <span style={{ flex: 1 }} />
          <Button
            size="sm"
            onClick={() => confirmar.mutate([...sel])}
            disabled={confirmar.isPending}
          >
            Aplicar em folha
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const motivo = prompt("Motivo da falha?");
              if (motivo && motivo.trim().length >= 3) falha.mutate({ ids: [...sel], motivo: motivo.trim() });
            }}
            disabled={falha.isPending}
          >
            Reportar falha
          </Button>
        </Card>
      ) : null}

      {/* Uma grid so'. O status virou filtro rapido (pedido do cliente
          29/08/2026) no lugar das tres secoes empilhadas — com paginacao,
          tres tabelas na mesma tela dariam tres paginadores concorrentes. */}
      <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: cab.cor }}>
          {cab.label} ({totalDoRecorte})
          <span style={{ marginLeft: 8, fontWeight: 400, color: "var(--text-dim)", letterSpacing: 0, textTransform: "none" }}>
            · {cab.hint}
          </span>
        </h2>
        <DataTable
          columns={columns}
          rows={daPagina}
          rowKey={(a) => a.id}
          loading={adfsQ.isLoading}
          emptyState={
            algumFiltroLigado
              ? "Nenhuma ADF bate com os filtros. Limpe os filtros para ver o resto da competência."
              : "Nenhuma ADF nova aguardando aplicação nesta competência."
          }
        />
        <Pagination
          total={totalDoRecorte}
          pagina={pagina}
          porPagina={porPagina}
          onPagina={setPagina}
          onPorPagina={setPorPagina}
          rotulo="ADFs"
        />
      </section>
    </div>
  );
}
